/**
 * Resizable review columns.
 *
 * The panel body in anchored-notes mode is
 *   `{playerW}px 6px minmax(300px,1fr) 6px {notesW}px`
 * with a draggable splitter between each pair. All of the geometry lives here
 * as pure functions plus a reducer so the clamps can be asserted without a
 * pointer; the hook at the bottom only wires events, persistence and viewport.
 *
 * A drag never re-renders on a pointer move: each move runs the same reducer
 * step off to the side and hands the widths to `onLiveWidths` (the panel
 * writes them straight onto its grid); the release commits that very step,
 * so state and screen agree once the drag ends.
 *
 * Widths are kept as the viewer's *intent* and the rendered widths are derived
 * from them for the current state (`resolveColumnWidths`). Closing the
 * conversation drops both the notes track and the panel's own width floor.
 * Clamping the stored value on every such transition would ratchet the
 * columns smaller each time the conversation is toggled — and persist the
 * shrinkage — so the stored value is never touched by anything except a
 * deliberate resize.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';

import { storage as appStorage } from '@discovr/common';

import { useBodyClasses } from '../../../../../lib/browser/useBodyClasses';
import { useWindowEvent } from '../../../../../lib/browser/useEventListener';
import { viewportWidth } from '../../../../../lib/browser/viewport';

/** Every column — player, AI review, notes — honours this floor. */
export const MIN_COLUMN_PX = 300;
/** Splitter width; one sits on each side of the AI review column. */
export const SPLITTER_PX = 6;
const SPLITTER_COUNT = 2;
const BOTH_SPLITTERS_PX = SPLITTER_PX * SPLITTER_COUNT;
/** The player's default width per aspect — the double-click reset targets. */
export const PLAYER_WIDTH_PORTRAIT_PX = 404;
export const PLAYER_WIDTH_LANDSCAPE_PX = 660;
/** The notes column's default. Other surfaces keep the legacy 360px chat column. */
export const NOTES_WIDTH_PX = 392;
/** One ←/→ press on a focused splitter. */
export const KEYBOARD_RESIZE_STEP_PX = 10;

// The columns are clamped against the panel, not the viewport, so the three of
// them always fit inside the sheet. Rather than measure the sheet, we evaluate
// the very CSS expression that sizes it (mirroring ContentReviewPanel's
// `panelWidth`) — a closed form of the viewport width, so it needs no
// ResizeObserver and stays unit-testable.

const PANEL_VIEWPORT_PERCENT = 72;
const PANEL_FLOOR_PX = 1240;
const PANEL_FLOOR_WITH_THREAD_PX = 1600;
const PANEL_GUTTER_REM = 3;
const REM_PX = 16;
const PANEL_GUTTER_PX = PANEL_GUTTER_REM * REM_PX;
/** Below this the sheet is `w-full!` — see the panel's `max-[992px]` class. */
const PANEL_FULL_WIDTH_MAX_PX = 992;
/** Used before the window is known (SSR / first paint in a test env). */
const PANEL_FALLBACK_VIEWPORT_PX = 1440;

/** The `style={{ width }}` the sheet carries, built from the same constants. */
export function panelWidthStyle(threadOpen: boolean): { width: string } {
  const floorPx = threadOpen ? PANEL_FLOOR_WITH_THREAD_PX : PANEL_FLOOR_PX;
  return { width: `min(max(${PANEL_VIEWPORT_PERCENT}vw, ${floorPx}px), calc(100vw - ${PANEL_GUTTER_REM}rem))` };
}

/** How wide the panel body actually is for a given viewport. */
export function panelAvailableWidth(innerWidth: number, threadOpen: boolean): number {
  if (innerWidth <= PANEL_FULL_WIDTH_MAX_PX) return innerWidth;
  const floorPx = threadOpen ? PANEL_FLOOR_WITH_THREAD_PX : PANEL_FLOOR_PX;
  const fluidPx = (innerWidth * PANEL_VIEWPORT_PERCENT) / 100;
  return Math.min(Math.max(fluidPx, floorPx), innerWidth - PANEL_GUTTER_PX);
}

/** The viewport width, or a typical desktop one where there is no window yet. */
function viewportWidthOrFallback(): number {
  return viewportWidth() || PANEL_FALLBACK_VIEWPORT_PX;
}

export type ColumnKind = 'player' | 'notes';

export interface ColumnWidths {
  playerW: number;
  notesW: number;
}

/** Everything the clamps need to know about the layout right now. */
export interface ColumnFit {
  availableWidth: number;
  /** The conversation is closed: the notes track AND its splitter are 0px. */
  notesCollapsed: boolean;
}

function splitterPxFor(fit: ColumnFit): number {
  return fit.notesCollapsed ? SPLITTER_PX : BOTH_SPLITTERS_PX;
}

/**
 * `max = innerWidth - other - 300 - 12`, floored at the 300px minimum so a
 * panel too narrow for three columns still returns a number rather than an
 * inverted range.
 */
export function maxColumnWidth(otherColumnPx: number, availableWidth: number, splitterPx: number = BOTH_SPLITTERS_PX): number {
  return Math.max(MIN_COLUMN_PX, availableWidth - otherColumnPx - MIN_COLUMN_PX - splitterPx);
}

export function clampColumnWidth(rawPx: number, otherColumnPx: number, availableWidth: number, splitterPx: number = BOTH_SPLITTERS_PX): number {
  return Math.max(MIN_COLUMN_PX, Math.min(maxColumnWidth(otherColumnPx, availableWidth, splitterPx), Math.round(rawPx)));
}

/**
 * What the *other* column actually costs this one. A collapsed notes track
 * renders at 0px, so reserving its stored width would shrink the player for a
 * column that is not on screen.
 */
export function effectiveOtherWidth(widths: ColumnWidths, column: ColumnKind, fit: ColumnFit): number {
  if (column === 'notes') return widths.playerW;
  return fit.notesCollapsed ? 0 : widths.notesW;
}

/**
 * The widths to render, derived from the viewer's stored intent.
 *
 * Below ~912px of panel the three 300px floors cannot all be met. Give way on
 * the notes column first (the reviewer is looking at the video), then on the
 * player. A collapsed notes column is left at its stored value — nothing
 * renders it, and it must come back unchanged.
 */
export function resolveColumnWidths(intent: ColumnWidths, fit: ColumnFit): ColumnWidths {
  const splitterPx = splitterPxFor(fit);
  const notesW = fit.notesCollapsed ? intent.notesW : clampColumnWidth(intent.notesW, intent.playerW, fit.availableWidth, splitterPx);
  const playerW = clampColumnWidth(intent.playerW, effectiveOtherWidth({ ...intent, notesW }, 'player', fit), fit.availableWidth, splitterPx);
  return { playerW, notesW };
}

export function defaultPlayerWidth(landscape: boolean): number {
  return landscape ? PLAYER_WIDTH_LANDSCAPE_PX : PLAYER_WIDTH_PORTRAIT_PX;
}

export const PLAYER_WIDTH_STORAGE_KEY = 'notes.layout.playerW';
export const NOTES_WIDTH_STORAGE_KEY = 'notes.layout.notesW';

/** The slice of the app's storage wrapper we use — narrow so tests can hand in a fake. */
export type WidthStorage = Pick<typeof appStorage, 'get' | 'set'>;

function parseStoredWidth(raw: string | null): number | undefined {
  if (raw === null) return undefined;
  const value = Number.parseInt(raw, 10);
  if (!Number.isFinite(value) || value <= 0) return undefined;
  return value;
}

/**
 * Both keys, best effort. A viewer with storage blocked (Safari private mode,
 * embedded webviews) must still get a working panel, so every failure degrades
 * to "nothing stored" rather than throwing through the render.
 */
export function readStoredWidths(storage: WidthStorage | null | undefined): Partial<ColumnWidths> {
  if (!storage) return {};
  try {
    const widths: Partial<ColumnWidths> = {};
    const playerW = parseStoredWidth(storage.get(PLAYER_WIDTH_STORAGE_KEY));
    const notesW = parseStoredWidth(storage.get(NOTES_WIDTH_STORAGE_KEY));
    if (playerW !== undefined) widths.playerW = playerW;
    if (notesW !== undefined) widths.notesW = notesW;
    return widths;
  } catch {
    return {};
  }
}

export function writeStoredWidths(storage: WidthStorage | null | undefined, widths: ColumnWidths): void {
  if (!storage) return;
  try {
    storage.set(PLAYER_WIDTH_STORAGE_KEY, String(widths.playerW));
    storage.set(NOTES_WIDTH_STORAGE_KEY, String(widths.notesW));
  } catch {
    // Storage is a convenience here (quota, blocked storage); losing it must never break the layout.
  }
}

/** A move not yet committed: where the pointer is, and the widths it gives. */
interface LiveMove {
  clientX: number;
  widths: ColumnWidths;
}

interface DragState {
  column: ColumnKind;
  startX: number;
  startW: number;
}

/** The viewer's intent, not what is on screen — see `resolveColumnWidths`. */
export interface ColumnLayoutState extends ColumnWidths {
  /** Live drag, or null. Also suppresses the width transition while dragging. */
  drag: DragState | null;
  /** The viewer has never sized the player column, so it still follows the video's aspect. */
  playerFollowsAspect: boolean;
  /** The viewer has changed something worth remembering for next time. */
  dirty: boolean;
  /** The saved widths have been read back; nothing is persisted before, or the defaults would overwrite them. */
  restored: boolean;
}

export type ColumnLayoutAction =
  /** `startW` is the width the viewer can see, so the drag never jumps on grab. */
  | { type: 'dragStart'; column: ColumnKind; clientX: number; startW: number }
  | { type: 'dragMove'; clientX: number; fit: ColumnFit }
  | { type: 'dragEnd' }
  | { type: 'nudge'; column: ColumnKind; deltaPx: number; fit: ColumnFit }
  | { type: 'reset'; column: ColumnKind; landscape: boolean }
  | { type: 'aspect'; landscape: boolean }
  | { type: 'restore'; stored: Partial<ColumnWidths> };

export function createColumnLayoutState(landscape: boolean): ColumnLayoutState {
  return {
    playerW: defaultPlayerWidth(landscape),
    notesW: NOTES_WIDTH_PX,
    drag: null,
    playerFollowsAspect: true,
    dirty: false,
    restored: false,
  };
}

function widthOf(widths: ColumnWidths, column: ColumnKind): number {
  return column === 'player' ? widths.playerW : widths.notesW;
}

function applyWidth(state: ColumnLayoutState, column: ColumnKind, widthPx: number): ColumnLayoutState {
  if (column === 'player') return { ...state, playerW: widthPx, playerFollowsAspect: false, dirty: true };
  return { ...state, notesW: widthPx, dirty: true };
}

/** A saved width outranks the default, and a saved player width outranks the aspect. */
function withStoredWidths(state: ColumnLayoutState, stored: Partial<ColumnWidths>): ColumnLayoutState {
  return {
    ...state,
    playerW: stored.playerW ?? state.playerW,
    notesW: stored.notesW ?? state.notesW,
    playerFollowsAspect: state.playerFollowsAspect && stored.playerW === undefined,
    restored: true,
  };
}

/**
 * A resize expressed the way the pointer moves: positive deltas travel right.
 * The player's splitter sits on its right edge, the notes' on its left, so the
 * notes column grows as the pointer moves left.
 */
function resizeColumn(state: ColumnLayoutState, column: ColumnKind, startW: number, pointerDeltaPx: number, fit: ColumnFit): ColumnLayoutState {
  const rawPx = column === 'player' ? startW + pointerDeltaPx : startW - pointerDeltaPx;
  const rendered = resolveColumnWidths(state, fit);
  const next = clampColumnWidth(rawPx, effectiveOtherWidth(rendered, column, fit), fit.availableWidth, splitterPxFor(fit));
  if (next === widthOf(state, column)) return state;
  return applyWidth(state, column, next);
}

export function columnLayoutReducer(state: ColumnLayoutState, action: ColumnLayoutAction): ColumnLayoutState {
  switch (action.type) {
    case 'dragStart':
      return { ...state, drag: { column: action.column, startX: action.clientX, startW: action.startW } };

    case 'dragMove': {
      const { drag } = state;
      // A move without a press — the splitter is only listening, not dragging.
      if (!drag) return state;
      const resized = resizeColumn(state, drag.column, drag.startW, action.clientX - drag.startX, action.fit);
      return resized === state ? state : { ...resized, drag };
    }

    case 'dragEnd':
      return state.drag ? { ...state, drag: null } : state;

    case 'nudge':
      return resizeColumn(state, action.column, widthOf(state, action.column), action.deltaPx, action.fit);

    case 'reset': {
      // The default is the intent; whether it fits right now is a render
      // concern, so a 660px landscape reset survives a narrow moment.
      const target = action.column === 'player' ? defaultPlayerWidth(action.landscape) : NOTES_WIDTH_PX;
      return target === widthOf(state, action.column) ? state : applyWidth(state, action.column, target);
    }

    case 'aspect': {
      // The intrinsic size arrives after the first frame decodes; adopt the
      // matching default only while the viewer has not chosen a width.
      if (!state.playerFollowsAspect) return state;
      const playerW = defaultPlayerWidth(action.landscape);
      return playerW === state.playerW ? state : { ...state, playerW };
    }

    case 'restore':
      // Once only: afterwards the in-memory intent is the newer of the two.
      if (state.restored) return state;
      return withStoredWidths(state, action.stored);

    default:
      return state;
  }
}

/** No text selection and one cursor for the whole page while a splitter is dragged. */
const RESIZING_BODY_CLASSES: readonly string[] = ['select-none', 'cursor-col-resize'];

export interface UseResizableColumnsArgs {
  /** Notes mode only; every other surface keeps the legacy fixed columns. */
  enabled: boolean;
  /** From `controller.intrinsic` — drives the double-click reset target. */
  landscape: boolean;
  /** The notes column is collapsed to 0 while the conversation is closed. */
  threadOpen: boolean;
  /** Injectable for tests; defaults to the app's localStorage wrapper. `null` persists nothing. */
  storage?: WidthStorage | null;
  /** The widths to paint mid-drag, before they are committed on release. Read live, so its identity is free to change. */
  onLiveWidths?: (widths: ColumnWidths) => void;
}

export interface ResizableColumns extends ColumnWidths {
  /** Widest this column may currently be — announced as `aria-valuemax`. */
  playerMaxW: number;
  notesMaxW: number;
  dragging: boolean;
  /** The splitter being dragged, or null — only that one paints its active state. */
  dragColumn: ColumnKind | null;
  startResize(column: ColumnKind, clientX: number): void;
  /** Mid-drag: the dragged column's live width, or undefined when no drag is on. */
  moveResize(clientX: number): number | undefined;
  endResize(): void;
  resetColumn(column: ColumnKind): void;
  nudgeColumn(column: ColumnKind, deltaPx: number): void;
}

export function useResizableColumns({ enabled, landscape, threadOpen, storage = appStorage, onLiveWidths }: UseResizableColumnsArgs): ResizableColumns {
  const [state, dispatch] = useReducer(columnLayoutReducer, landscape, createColumnLayoutState);

  // Storage is read once, the first time notes mode is on — a cold open mounts
  // this hook before the notes arrive. Layout, so the saved widths are what
  // paints first rather than animating in from the defaults.
  useLayoutEffect(() => {
    if (!enabled || state.restored) return;
    dispatch({ type: 'restore', stored: readStoredWidths(storage) });
  }, [enabled, state.restored, storage]);

  const [viewportPx, setViewportPx] = useState(viewportWidthOrFallback);
  const syncViewport = useCallback(() => setViewportPx(viewportWidthOrFallback()), []);
  useWindowEvent('resize', syncViewport, { enabled });
  // The viewport may have changed while notes mode was off and not listening:
  // catch up as the listener starts.
  useEffect(() => {
    if (!enabled) return;
    const catchUp = () => setViewportPx(viewportWidthOrFallback());
    catchUp();
  }, [enabled]);

  const fit = useMemo<ColumnFit>(
    () => ({ availableWidth: panelAvailableWidth(viewportPx, threadOpen), notesCollapsed: !threadOpen }),
    [viewportPx, threadOpen],
  );
  const rendered = useMemo(() => resolveColumnWidths(state, fit), [state, fit]);

  // The pointer handlers stay stable across renders, so they read the live state,
  // fit, widths and aspect through a ref — written after commit, never during render.
  const latestRef = useRef({ state, fit, rendered, landscape, onLiveWidths });
  useLayoutEffect(() => {
    latestRef.current = { state, fit, rendered, landscape, onLiveWidths };
  });
  // The last move of the drag in progress and the widths it paints, committed on release.
  const liveMoveRef = useRef<LiveMove | null>(null);
  // The drag as pressed, set at once: a move can arrive before the dragStart render commits `state.drag`.
  const dragRef = useRef<DragState | null>(null);

  const startResize = useCallback((column: ColumnKind, clientX: number) => {
    liveMoveRef.current = null;
    const startW = widthOf(latestRef.current.rendered, column);
    dragRef.current = { column, startX: clientX, startW };
    dispatch({ type: 'dragStart', column, clientX, startW });
  }, []);
  // Every move is measured from the drag's start, so running the reducer on the
  // committed (drag-start) state gives what committing this move would.
  const moveResize = useCallback((clientX: number) => {
    const latest = latestRef.current;
    const drag = dragRef.current;
    if (!drag) return undefined;
    const pressed = { ...latest.state, drag };
    const live = resolveColumnWidths(columnLayoutReducer(pressed, { type: 'dragMove', clientX, fit: latest.fit }), latest.fit);
    liveMoveRef.current = { clientX, widths: live };
    latest.onLiveWidths?.(live);
    return widthOf(live, drag.column);
  }, []);
  // Called by the splitter and by the window (a release outside it): only the first commits.
  const endResize = useCallback(() => {
    const live = liveMoveRef.current;
    liveMoveRef.current = null;
    dragRef.current = null;
    if (live) dispatch({ type: 'dragMove', clientX: live.clientX, fit: latestRef.current.fit });
    dispatch({ type: 'dragEnd' });
  }, []);

  // Anything that re-renders mid-drag (a window resize, the video pausing)
  // paints the committed widths: put the live ones back before the browser paints.
  useLayoutEffect(() => {
    const live = liveMoveRef.current;
    if (!live || !state.drag) return;
    latestRef.current.onLiveWidths?.(live.widths);
  });
  const resetColumn = useCallback((column: ColumnKind) => dispatch({ type: 'reset', column, landscape: latestRef.current.landscape }), []);
  const nudgeColumn = useCallback((column: ColumnKind, deltaPx: number) => dispatch({ type: 'nudge', column, deltaPx, fit: latestRef.current.fit }), []);

  const dragging = state.drag !== null;

  // A pointer released outside the splitter (or a window that loses focus
  // mid-drag) must still end the drag, or the page keeps its resize cursor.
  useWindowEvent('pointerup', endResize, { enabled: dragging });
  useWindowEvent('pointercancel', endResize, { enabled: dragging });
  useWindowEvent('blur', endResize, { enabled: dragging });

  useBodyClasses(RESIZING_BODY_CLASSES, dragging);

  useEffect(() => {
    if (!enabled) return;
    dispatch({ type: 'aspect', landscape });
  }, [enabled, landscape]);

  // Persist only what the viewer actually chose, and only once the drag settles
  // — a live drag would otherwise write on every pointer move. These are the
  // stored intents, so a narrow panel never writes back its own clamping.
  useEffect(() => {
    if (!enabled || !state.restored || !state.dirty || dragging) return;
    writeStoredWidths(storage, { playerW: state.playerW, notesW: state.notesW });
  }, [enabled, state.restored, state.dirty, dragging, state.playerW, state.notesW, storage]);

  const splitterPx = splitterPxFor(fit);
  return {
    playerW: rendered.playerW,
    notesW: rendered.notesW,
    playerMaxW: maxColumnWidth(effectiveOtherWidth(rendered, 'player', fit), fit.availableWidth, splitterPx),
    notesMaxW: maxColumnWidth(effectiveOtherWidth(rendered, 'notes', fit), fit.availableWidth, splitterPx),
    dragging,
    dragColumn: state.drag?.column ?? null,
    startResize,
    moveResize,
    endResize,
    resetColumn,
    nudgeColumn,
  };
}
